import config from './config';
import { fetchWithTimeout, handleApiError, validateEmail } from './utils';

// Authentication functions
export const LoginWithEmailAddress = async (email, password) => {
  if (!validateEmail(email)) {
    throw new Error('Adresse email invalide. Elle doit comporter entre 3 et 20 caractères alphanumériques.');
  }

  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/auth/LoginWithEmailAddress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({
        Email: email,
        Password: password,
        CreateAccount: false,
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      // Store auth data in localStorage with the same keys for compatibility
      localStorage.setItem('playFabId', data.data.LoginResult.PlayFabId);
      localStorage.setItem('sessionTicket', data.data.LoginResult.SessionTicket);
      return data;
    } else {
      throw new Error(data.error || 'Échec de la connexion.');
    }
  } catch (error) {
    throw new Error(handleApiError(error, 'Erreur lors de la connexion'));
  }
};

export const RegisterPlayFabUser = async (email, password, displayName) => {
  if (!validateEmail(email)) {
    throw new Error('Adresse email invalide.');
  }

  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/auth/RegisterPlayFabUser`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({
        Email: email,
        Password: password,
        Username: displayName.trim(),
        CreateAccount: true,
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      return {
        success: true,
        data: data.data,
      };
    } else {
      return {
        success: false,
        errorMessage: data.error || 'Failed to create account',
      };
    }
  } catch (error) {
    return {
      success: false,
      errorMessage: handleApiError(error, 'Failed to create account'),
    };
  }
};

// Helper functions for authentication
export const getSessionTicket = () => {
  const sessionTicket = localStorage.getItem('sessionTicket');
  if (!sessionTicket) {
    throw new Error('No session ticket found in local storage');
  }
  return sessionTicket;
};

export const getPlayFabId = () => {
  const playFabId = localStorage.getItem('playFabId');
  if (!playFabId) {
    throw new Error('No PlayFab ID found in local storage');
  }
  return playFabId;
};

export const setPlayFabId = (playFabId) => {
  localStorage.setItem('playFabId', playFabId);
};

export const setSessionTicket = (sessionTicket) => {
  localStorage.setItem('sessionTicket', sessionTicket);
};

// Profile functions
export async function getPlayerProfile(playFabId) {
  const sessionTicket = getSessionTicket();
  
  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/GetPlayerProfile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-App-Id': config.appId,
        'X-Authentication': sessionTicket,
      },
      body: JSON.stringify({}),
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to get player profile');
    }

    const profile = data.data.Profile;
    
    return {
      playFabId: profile.PlayFabId,
      displayName: profile.DisplayName || 'Utilisateur'
    };
  } catch (error) {
    console.error('Error:', error);
    return { playFabId: null, displayName: 'Utilisateur' };
  }
}

export const getPlayerStatistics = async () => {
  const sessionTicket = getSessionTicket();

  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/GetPlayerStatistics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({}),
    });

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to get player statistics');
    }
    
    return data;
  } catch (error) {
    throw new Error(handleApiError(error, 'Failed to get player statistics'));
  }
};

export const updateDisplayName = async (playFabId, displayName) => {
  const sessionTicket = getSessionTicket();
  
  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/UpdateUserTitleDisplayName`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({
        PlayFabId: playFabId,
        DisplayName: displayName.trim(),
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      return { success: true };
    } else {
      return {
        success: false,
        errorMessage: data.error || 'Failed to update display name',
      };
    }
  } catch (error) {
    return {
      success: false,
      errorMessage: handleApiError(error, 'Failed to update display name'),
    };
  }
};

// For backward compatibility
export const getAccountInfo = async (playFabId) => {
  return getPlayerProfile(playFabId);
};

// Quiz/Survey functions
export const GetQuizInstances = async (QuizInstanceId, MatchId) => {
  const sessionTicket = getSessionTicket();
  console.log('Session Ticket récupéré:', sessionTicket);

  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/GetQuizInstances`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({
        QuizInstanceId,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const data = await response.json();
    console.log('API response:', data);
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to get quiz instances');
    }

    // Transform the data to match the expected format
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const validInstances = data.data.QuizInstances.map(instance => ({
      instanceId: instance.instanceId,
      displayName: instance.title,
      voteExpiration: new Date(instance.end_time || Date.now() + 3600000).getTime() / 1000,
      timestamp: new Date(instance.start_time).getTime() / 1000,
      questions: instance.questions,
      jsonUrl: instance.json_url,
      isAnswered: instance.isAnswered
    }));

    return {
      success: data.success,
      validInstances: validInstances.map(instance => ({
        ...instance,
        timeRemaining: instance.voteExpiration - currentTimestamp,
      })),
    };
  } catch (error) {
    console.error('Error fetching quiz instances:', error.message);
    return {
      success: false,
      error: handleApiError(error, 'Failed to get quiz instances'),
    };
  }
};

export const getSurveyInstances = async (SurveyInstanceId) => {
  // For backward compatibility, we'll use the same endpoint as GetQuizInstances
  return GetQuizInstances(SurveyInstanceId);
};

export const GetTriviaInstances = async (triviaInstanceId, MatchId) => {
  // For backward compatibility, we'll use the same endpoint as GetQuizInstances
  return GetQuizInstances(triviaInstanceId);
};

export const AnswerSurvey = async (QuizInstanceId, QuestionId, TimeToAnswer, SelectedAnswer) => {
  const sessionTicket = getSessionTicket();
  console.log('Session Ticket for AnswerSurvey:', sessionTicket);

  if (!sessionTicket) {
    console.error('Invalid or missing session ticket.');
    return {
      success: false,
      error: 'Invalid or missing session ticket.',
    };
  }
  
  // Format the request body to match what the backend expects
  const requestBody = {
    InstanceId: QuizInstanceId,
    Answers: [{ 
      QuestionId: QuestionId, 
      SelectedAnswer: SelectedAnswer 
    }],
    TimeToAnswer: TimeToAnswer,
  };
  
  console.log('Request body:', requestBody);
  
  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/AnswerSurvey`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorDetails = await response.text(); 
      throw new Error(`HTTP error: ${response.status} - ${errorDetails}`);
    }

    const data = await response.json();
    console.log('Response data:', data);

    return {
      success: true,
      data: data.data || {},
    };
  } catch (error) {
    console.error('Error submitting survey answer:', error.message);
    return {
      success: false,
      error: handleApiError(error, 'Failed to submit survey answer'),
    };
  }
};

// Leaderboard functions
export async function getLeaderboard() {
  const sessionTicket = getSessionTicket();

  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/GetLeaderboard`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({
        StatisticName: 'total_score',
        StartPosition: 0,
        MaxResultsCount: 30,
        ProfileConstraints: {
          DisplayName: true,
          AvatarUrl: true,
          Statistics: true,
          Position: true,
        }
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching leaderboard:', error.message);
    throw new Error(handleApiError(error, 'Failed to fetch leaderboard'));
  }
}

// User data functions
export const updateUserData = async (playFabId, historiqueSuggestions) => {
  const sessionTicket = getSessionTicket();
  
  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/UpdateUserData`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({
        PlayFabId: playFabId,
        Data: {
          historiqueSuggestions: JSON.stringify(historiqueSuggestions)
        }
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      return data;
    } else {
      throw new Error(data.error || 'Failed to update user data');
    }
  } catch (error) {
    console.error('Error in UpdateUserData:', error);
    throw new Error(handleApiError(error, 'Failed to update user data'));
  }
};

export const getUserData = async (playFabId) => {
  const sessionTicket = getSessionTicket();
  
  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/GetUserData`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({
        PlayFabId: playFabId
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      if (data.data && data.data.Data && data.data.Data.historiqueSuggestions) {
        const historique = JSON.parse(data.data.Data.historiqueSuggestions);
        console.log('Historique des suggestions chargé:', historique);
        return historique;
      } else {
        console.log('Aucune suggestion trouvée.');
        return [];
      }
    } else {
      throw new Error(data.error || 'Failed to get user data');
    }
  } catch (error) {
    console.error('Error in GetUserData:', error);
    return [];
  }
};

// Avatar functions
export const updateAvatarUrl = async (playFabId, imageUrl) => {
  const sessionTicket = getSessionTicket();
  
  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/UpdateAvatarUrl`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({
        PlayFabId: playFabId,
        ImageUrl: imageUrl
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      return data;
    } else {
      throw new Error(data.error || 'Failed to update avatar URL');
    }
  } catch (error) {
    console.error('Error in updateAvatarUrl:', error);
    throw new Error(handleApiError(error, 'Failed to update avatar URL'));
  }
};

// Contact email functions
export const updateUserContactEmail = async (playFabId, contactEmail) => {
  const sessionTicket = getSessionTicket();
  
  try {
    const response = await fetchWithTimeout(`${config.apiBaseUrl}/client/UpdateUserContactEmail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId,
      },
      body: JSON.stringify({
        PlayFabId: playFabId,
        ContactEmail: contactEmail.trim()
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      return { success: true };
    } else {
      return {
        success: false,
        errorMessage: data.error || 'Failed to update contact email',
      };
    }
  } catch (error) {
    return {
      success: false,
      errorMessage: handleApiError(error, 'Failed to update contact email'),
    };
  }
};

// SSE setup for real-time events
export const setupEventSource = () => {
  const sessionTicket = localStorage.getItem('sessionTicket');
  if (!sessionTicket) return null;
  
  let retryCount = 0;
  const maxRetries = 5;
  const retryDelay = 3000; // 3 seconds
  
  // Try to use SSE first
  try {
    console.log('Attempting to connect via SSE...');
    const nativeEventSource = new EventSource(
      `${config.apiBaseUrl}/events?token=${encodeURIComponent(sessionTicket)}`
    );
    
    // Set up event handlers
    nativeEventSource.onopen = () => {
      console.log('SSE connection established successfully');
      retryCount = 0;
      
      // Store the last sequence number for fallback
      const lastSequenceNumber = localStorage.getItem('lastEventSequence') || '0';
      console.log(`Last sequence number: ${lastSequenceNumber}`);
      
      // If we have a sequence number > 0, sync any missed events
      if (parseInt(lastSequenceNumber, 10) > 0) {
        syncMissedEvents(sessionTicket, parseInt(lastSequenceNumber, 10));
      }
    };
    
    nativeEventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      
      if (retryCount >= maxRetries) {
        console.log('Max SSE retry attempts reached, falling back to polling');
        nativeEventSource.close();
        
        // Fall back to polling
        return setupPolling(sessionTicket);
      }
      
      retryCount++;
      console.log(`SSE reconnection attempt ${retryCount}/${maxRetries}`);
    };
    
    return nativeEventSource;
  } catch (error) {
    console.error('Error setting up SSE:', error);
    console.log('Falling back to polling approach');
    
    // Fall back to polling
    return setupPolling(sessionTicket);
  }
};

// Helper function to sync missed events
const syncMissedEvents = async (sessionTicket, lastSequenceNumber) => {
  try {
    console.log(`Syncing missed events since sequence ${lastSequenceNumber}`);
    
    const response = await fetch(`${config.apiBaseUrl}/client/SyncEvents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authentication': sessionTicket,
        'X-App-Id': config.appId
      },
      body: JSON.stringify({
        lastSequenceNumber: lastSequenceNumber
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success && data.data.events && data.data.events.length > 0) {
      console.log(`Received ${data.data.events.length} missed events`);
      
      // Store the new last sequence number
      localStorage.setItem('lastEventSequence', data.data.lastSequenceNumber.toString());
      
      // Process each event
      data.data.events.forEach(event => {
        const messageEvent = new CustomEvent('event-received', {
          detail: {
            EventName: event.type,
            ...event.data
          }
        });
        
        window.dispatchEvent(messageEvent);
      });
    } else {
      console.log('No missed events to sync');
    }
  } catch (error) {
    console.error('Error syncing missed events:', error);
  }
};

// Fallback polling approach
const setupPolling = (sessionTicket) => {
  console.log('Setting up polling fallback for events');
  
  const eventListeners = [];
  let intervalId = null;
  let retryCount = 0;
  const maxRetries = 5;
  
  const eventSource = {
    onmessage: null,
    onerror: null,
    
    addEventListener: (type, callback) => {
      if (type === 'message') {
        eventListeners.push(callback);
      }
    },
    
    removeEventListener: (type, callback) => {
      if (type === 'message') {
        const index = eventListeners.indexOf(callback);
        if (index !== -1) {
          eventListeners.splice(index, 1);
        }
      }
    },
    
    close: () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }
  };
  
  // Start polling for events
  const pollEvents = async () => {
    try {
      // Get the last sequence number from localStorage
      const lastSequenceNumber = localStorage.getItem('lastEventSequence') || '0';
      
      // Fetch events since last sequence number
      const response = await fetch(`${config.apiBaseUrl}/client/SyncEvents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Authentication': sessionTicket,
          'X-App-Id': config.appId
        },
        body: JSON.stringify({
          lastSequenceNumber: parseInt(lastSequenceNumber, 10)
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data.events && data.data.events.length > 0) {
        console.log(`Polling received ${data.data.events.length} events`);
        
        // Store the new last sequence number
        localStorage.setItem('lastEventSequence', data.data.lastSequenceNumber.toString());
        
        // Process each event
        data.data.events.forEach(event => {
          const messageEvent = {
            data: JSON.stringify({
              EventName: event.type,
              ...event.data
            })
          };
          
          // Call onmessage handler
          if (eventSource.onmessage) {
            eventSource.onmessage(messageEvent);
          }
          
          // Call event listeners
          eventListeners.forEach(listener => {
            listener(messageEvent);
          });
        });
      }
      
      // Reset retry count on successful connection
      retryCount = 0;
    } catch (error) {
      console.error('Error polling events:', error);
      
      // Call onerror handler
      if (eventSource.onerror) {
        eventSource.onerror(error);
      }
      
      // Implement reconnection logic
      if (retryCount < maxRetries) {
        retryCount++;
        console.log(`Polling reconnection attempt ${retryCount}/${maxRetries}`);
      } else {
        console.error('Max polling reconnection attempts reached');
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    }
  };
  
  // Poll immediately and then at regular intervals (once per second)
  pollEvents();
  intervalId = setInterval(pollEvents, 1000); // Poll every 1 second
  
  return eventSource;
};
